import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { DocumentIcon, FolderIcon } from '../icons';
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, doc, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import PageHeader from '../components/PageHeader';
import DocumentCollaborationStatus from '../components/DocumentCollaborationStatus';
import { toast } from 'react-toastify';
import { FileOutlined, FilePdfOutlined, FileWordOutlined, FileExcelOutlined, FilePptOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { Tooltip, Button } from 'antd';
import signedUrlService from '../services/signedUrlService';
import { getDocumentStoragePath, constructOrgDocumentPath } from '../utils/storagePathExtractor';
import { 
  createEditSession, 
  generateSmartDownloadUrl, 
  updateEditSession,
  completeEditSession 
} from '../services/documentEditService';

export default function Documents() {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const getFileIcon = (fileType) => {
    if (!fileType) return <FileOutlined className="me-2" />;
    
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return <FilePdfOutlined className="me-2" style={{ color: '#d32f2f' }} />;
    if (type.includes('word') || type.includes('doc')) return <FileWordOutlined className="me-2" style={{ color: '#1976d2' }} />;
    if (type.includes('excel') || type.includes('sheet') || type.includes('xls')) return <FileExcelOutlined className="me-2" style={{ color: '#388e3c' }} />;
    if (type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) return <FilePptOutlined className="me-2" style={{ color: '#f57c00' }} />;
    return <FileOutlined className="me-2" />;
  };

  const isOfficeFile = (fileType) => {
    if (!fileType) return false;
    const type = fileType.toLowerCase();
    return type.includes('word') || type.includes('doc') || 
           type.includes('excel') || type.includes('sheet') || type.includes('xls') ||
           type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt');
  };

  /**
   * Enhanced edit workflow for Office documents
   */
  const handleEditDocument = async (docData) => {
    try {
      if (!docData || !currentUser) {
        toast.error('Document or user information not available');
        return;
      }

      console.log('📝 Starting enhanced edit workflow for:', docData.name);

      // Create edit session
      const sessionId = await createEditSession(
        docData.id,
        currentUser.uid,
        {
          name: currentUser.displayName || `${currentUser.firstName} ${currentUser.lastName}`,
          email: currentUser.email,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName
        }
      );

      // Generate smart download info and trigger download
      const smartDownloadInfo = generateSmartDownloadUrl(docData, sessionId);
      
      // Update edit session to mark download
      await updateEditSession(sessionId, {
        downloadedAt: new Date(),
        status: 'active'
      });

      // Trigger download
      const downloadUrl = docData.url;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = docData.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Edit session started! File downloaded for editing in Office.');

    } catch (error) {
      console.error('❌ Error starting edit workflow:', error);
      toast.error('Failed to start edit session. Please try again.');
    }
  };

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      if (!currentUser?.orgId) {
        setDocuments([]);
        return;
      }

      // Query for organisation-level documents (no cmtId filter)
      const q = query(
        collection(db, 'c3_documents'),
        where('orgId', '==', currentUser.orgId),
        where('isOrgLevel', '==', true), // Only org-level documents
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const documentList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDocuments(documentList);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.orgId, db]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const storage = getStorage();
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `documents/${currentUser.orgId}/org-level/${fileName}`);

      // Upload file
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Add document record to Firestore
      await addDoc(collection(db, 'c3_documents'), {
        name: file.name,
        type: file.type,
        size: file.size,
        url: downloadURL,
        orgId: currentUser.orgId,
        isOrgLevel: true, // Mark as organisation-level document
        uploadedBy: currentUser.email,
        uploadedByUid: currentUser.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        version: 1,
        versions: [{
          version: 1,
          url: downloadURL,
          uploadedBy: currentUser.email,
          uploadedByUid: currentUser.uid,
          uploadedAt: new Date(),
          fileName: fileName
        }],
        isFolder: false
      });

      loadDocuments();
      toast.success('Document uploaded successfully');
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpdate = async (document, newFile) => {
    try {
      setUploading(true);
      const storage = getStorage();
      const timestamp = Date.now();
      const fileName = `${timestamp}_${newFile.name}`;
      const storageRef = ref(storage, `documents/${currentUser.orgId}/org-level/${fileName}`);

      // Upload new version
      await uploadBytes(storageRef, newFile);
      const downloadURL = await getDownloadURL(storageRef);

      // Update document record in Firestore
      const newVersion = {
        version: document.version + 1,
        url: downloadURL,
        uploadedBy: currentUser.email,
        uploadedByUid: currentUser.uid,
        uploadedAt: new Date(),
        fileName: fileName
      };

      await updateDoc(doc(db, 'c3_documents', document.id), {
        url: downloadURL,
        updatedAt: new Date(),
        version: newVersion.version,
        versions: [...document.versions, newVersion]
      });

      loadDocuments();
      toast.success('Document updated successfully');
    } catch (err) {
      console.error('Error updating file:', err);
      setError('Failed to update file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleViewDocument = (documentId) => {
    navigate(`/documents/view/${documentId}`);
  };

  const handleDownloadDocument = async (document) => {
    try {
      console.log('🔐 Generating signed URL for download:', document.name);
      
      // Get storage path using utility
      const fallbackFileName = document.versions?.[document.versions.length - 1]?.fileName || document.name;
      const fallbackPath = constructOrgDocumentPath(currentUser.orgId, fallbackFileName);
      const storagePath = getDocumentStoragePath(document, fallbackPath);

      // Generate signed URL for download
      const signedUrl = await signedUrlService.generateSignedUrl(storagePath, 15);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = document.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ Download initiated successfully');
      
    } catch (error) {
      console.error('❌ Error downloading document:', error);
      toast.error(`Failed to download ${document.name}. Please try again.`);
      
      // Fallback to direct URL
      try {
        window.open(document.url, '_blank');
      } catch (fallbackError) {
        console.error('❌ Fallback download also failed:', fallbackError);
        toast.error('Unable to download document. Please contact support.');
      }
    }
  };

  return (
    <div className="p-4">
      <PageHeader title="Documents" />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-1">Organisation Documents</h5>
              <p className="text-muted mb-0">Manage documents available to your entire organisation</p>
            </div>
            <Tooltip title="Upload a new document to your organization's document library">
              <label className="btn btn-primary" style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}>
                <input
                  type="file"
                  className="d-none"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                {uploading ? 'Uploading...' : 'Upload Document'}
              </label>
            </Tooltip>
          </div>

          {/* File List */}
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Last Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          {getFileIcon(doc.type)}
                          <div>
                            <div style={{ fontWeight: '500' }}>{doc.name}</div>
                            <small className="text-muted">
                              Uploaded by {doc.uploadedBy}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>{doc.type}</td>
                      <td>{formatFileSize(doc.size)}</td>
                      <td>
                        <span style={{ fontWeight: '500' }}>v{doc.version}</span>
                      </td>
                      <td>
                        <DocumentCollaborationStatus 
                          documentId={doc.id}
                          compact={true}
                          showVersionInfo={false}
                        />
                      </td>
                      <td>{doc.updatedAt?.toDate ? doc.updatedAt.toDate().toLocaleDateString() : new Date(doc.updatedAt).toLocaleDateString()}</td>
                      <td>
                        <div className="d-flex gap-1">
                          {isOfficeFile(doc.type) && (
                            <Tooltip title="Edit in your desktop Office application">
                              <Button
                                size="small"
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => handleEditDocument(doc)}
                                style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
                              >
                                Edit
                              </Button>
                            </Tooltip>
                          )}
                          <Tooltip title="View document details and preview">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleViewDocument(doc.id)}
                              style={{ borderColor: '#39325b', color: '#39325b' }}
                            >
                              <EyeOutlined />
                            </button>
                          </Tooltip>
                          <Tooltip title="Download document to your device">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleDownloadDocument(doc)}
                              style={{ borderColor: '#39325b', color: '#39325b' }}
                            >
                              Download
                            </button>
                          </Tooltip>
                          <Tooltip title="Upload a new version of this document">
                            <label className="btn btn-sm btn-outline-primary" style={{ borderColor: '#39325b', color: '#39325b' }}>
                              <input
                                type="file"
                                className="d-none"
                                onChange={(e) => handleFileUpdate(doc, e.target.files[0])}
                                disabled={uploading}
                              />
                              Update
                            </label>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {documents.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div>
                          <FileOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
                          <h6>No organisation documents found</h6>
                          <p className="text-muted mb-0">Upload your first document to get started with collaborative editing.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
} 